// Styles
import "./TasksTab.scss";

// React
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Icons
import ChevronLeftIcon from "../../_assets/icons/ChevronLeftIcon";
import ChevronRightIcon from "../../_assets/icons/ChevronRightIcon";
import FirstPageIcon from "../../_assets/icons/FirstPageIcon";
import LastPageIcon from "../../_assets/icons/LastPageIcon";

// UI Components
import { Label2, Subtitle2, Icon } from "@bms-ai/bms-ai-ui-lib/components";
import { getTaskResults } from "../../api/data-services/task-api";
import type { TaskResults } from "../../pages/qa-results-new/QAResultsNew";
import { useSession } from "../../context/session-context";
import { useQATask } from "../../context/QATaskContext";
import { downloadPDFReport } from "../../api/data-services/results-api";
import { formatChannelName } from "../../helpers/helpers";

// Types
interface Task {
  task_id: string;
  file_name: string;
  brand: string;
  channel: string;
  date: string;
  compliance_score: number;
  status: "Compliant" | "Needs Review";
  issues: number;
}

interface TasksTabProps {
  tasks: Task[] | null;
  isTasksLoading: boolean;
  currentPage: number;
  totalPages: number;
  //   totalCount: number;
  onPageChange: (page: number) => void;
}

const TasksTab: React.FC<TasksTabProps> = ({
  tasks,
  isTasksLoading,
  currentPage,
  totalPages,
  //   totalCount,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const [activeMenuTaskId, setActiveMenuTaskId] = useState<string | null>(null);
  const { moduleAccessToken, sessionDetails } = useSession();
  const { setStatus, setTaskResults, setTaskId, setResults } = useQATask();
  //   const getPageRange = () => {
  //     if (!tasks || tasks.length === 0) {
  //       return { startIndex: 0, endIndex: 0, totalItems: totalCount };
  //     }
  //     const pageSize = 5;
  //     const startIndex = (currentPage - 1) * pageSize + 1;
  //     const endIndex = Math.min(currentPage * pageSize, totalCount);
  //     return { startIndex, endIndex, totalItems: totalCount };
  //   };

  const toggleMenu = (taskId: string) => {
    setActiveMenuTaskId(activeMenuTaskId === taskId ? null : taskId);
  };

  const handleViewTask = async (taskId: string) => {
    console.log("View task:", taskId);

    setTaskId(taskId);
    const resultsResponse: TaskResults = await getTaskResults(
      taskId!,
      moduleAccessToken,
    );
    if (resultsResponse.outputs_json.is_last_consignment === undefined) {
      resultsResponse.outputs_json.is_last_consignment = true;
    }
    setResults(resultsResponse);
    setTaskResults(resultsResponse);
    setStatus("completed");
    // Navigate to QA results page with the task ID
    navigate(`/qa-results`);
    setActiveMenuTaskId(null);
  };

  const handleDownloadReport = async (taskId: string) => {
    // Implement download report logic
    console.log("Download report for task:", taskId);
    if (!taskId) {
      console.error("No task ID available for download");
      return;
    }

    try {
      await downloadPDFReport(
        taskId,
        sessionDetails.client_id || "",
        moduleAccessToken,
      );
      console.log("PDF download completed");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to download PDF report. Please try again.",
      );
    }

    setActiveMenuTaskId(null);
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case "Compliant":
        return "status-compliant";
      case "Needs Review":
        return "status-needs-review";
      default:
        return "";
    }
  };

  if (isTasksLoading) {
    return (
      <div className="tasks-container">
        <div className="loading-state">
          <Subtitle2>Loading tasks...</Subtitle2>
        </div>
      </div>
    );
  }

  if (!tasks || tasks.length === 0) {
    return (
      <div className="tasks-container">
        <div className="no-data-state">
          <Subtitle2>No tasks available</Subtitle2>
        </div>
      </div>
    );
  }

  return (
    <div className="tasks-container">
      {/* Tasks Table */}
      <div className="tasks-table-container">
        <table className="tasks-table">
          <thead>
            <tr>
              <th>
                <div className="header-content">
                  <span>Task ID</span>
                </div>
              </th>
              <th>
                <div className="header-content">
                  <span>Brand</span>
                </div>
              </th>
              <th>
                <div className="header-content">
                  <span>Channel</span>
                </div>
              </th>
              <th>
                <div className="header-content">
                  <span>Date</span>
                </div>
              </th>
              <th>
                <div className="header-content">
                  <span>Status Compliance</span>
                </div>
              </th>
              <th>
                <div className="header-content">
                  <span>Issues</span>
                </div>
              </th>
              <th className="actions-header"></th>
            </tr>
          </thead>
          <tbody>
            {tasks &&
              tasks.map((task) => (
                <tr key={task.task_id}>
                  <td>
                    <Label2>{task.task_id}</Label2>
                  </td>
                  <td>
                    <Label2>{task.brand}</Label2>
                  </td>
                  <td>
                    <Label2>{formatChannelName(task.channel)}</Label2>
                  </td>
                  <td>
                    <Label2>{task.date}</Label2>
                  </td>
                  <td>
                    <div className="status-compliance-cell">
                      <span
                        className={`status-badge ${getStatusClass(task.compliance_score >= 90 ? "Compliant" : "Needs Review")}`}
                      >
                        {task.compliance_score >= 90
                          ? "Excellent"
                          : task.compliance_score >= 75
                            ? "Needs Review"
                            : "Critical"}
                      </span>
                      <span className="compliance-score">
                        {task.compliance_score > 0
                          ? task.compliance_score.toFixed(1)
                          : 0}
                        %
                      </span>
                    </div>
                  </td>
                  <td>
                    <Label2>{task.issues}</Label2>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button
                        className="actions-menu-btn"
                        onClick={() => toggleMenu(task.task_id)}
                      >
                        <Icon type="more" size="small" />
                      </button>
                      {activeMenuTaskId === task.task_id && (
                        <div className="actions-menu">
                          <button
                            className="menu-item"
                            onClick={() => handleViewTask(task.task_id)}
                          >
                            View
                          </button>
                          <button
                            className="menu-item"
                            onClick={() => handleDownloadReport(task.task_id)}
                          >
                            Download Report
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="tasks-pagination">
            <div className="pagination-info">
              {/* <span>
                {getPageRange().startIndex} to {getPageRange().endIndex} of{" "}
                {getPageRange().totalItems}
              </span> */}
            </div>
            <div className="pagination-controls">
              <button
                className="page-nav-btn"
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                title="First page"
              >
                <FirstPageIcon width={16} height={16} />
              </button>
              <button
                className="page-nav-btn"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <ChevronLeftIcon width={16} height={16} />
              </button>
              <span className="page-indicator">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="page-nav-btn"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <ChevronRightIcon width={16} height={16} />
              </button>
              <button
                className="page-nav-btn"
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last page"
              >
                <LastPageIcon width={16} height={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksTab;
